import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../utils/safeToast';
import { useAuth } from '../context/AuthContext';

const API = "/api";

// Polling-Intervall in Millisekunden (30 Sekunden)
const POLL_INTERVAL = 30000;

// Benachrichtigungs-Sound als Base64 (dezenter "ding" Sound)
const NOTIFICATION_SOUND_BASE64 = `data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYwfzPxAAAAAAD/+9DEAAAGAAGn9AAAIl0Jtv81kBBDgVzc38QBAMBAIGf/E+D5+XLl/ggAACH/+H4fn5/y/E7/xP/E/+J+X//l+cAAAAAAAAAOoGAwGRQKPDAYDAYDQFAsJAwDA+DgeDwuHhYfFg8HAoFA4OBQKBR4YHg8HBwcHhgODgcDweDgeDwdRRRRRRRUYoLB4YHAoMxQOB4ODgoIAsJgoFAiCYoEwmGBwPDAsH0wwmH4+mGxPn4/xPxPi+f5+fj5/y/OP5+fn4nn5+f5x/Pz/n+J+fn5x/n5+c/8/z8/P8/P/PzL6AAAAsAAB2AAJGhVKi//5+hVKioqKioqLpaioqK6uqqqqqqqqqqqqioqKiyWqqq66kMDAwMDEwwMDAwMDAzMDAwMDAzP//tQxAMAFfQ9sfmNADKEg66/N4AAMDAwMD/4wMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDA3/MYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxhpjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxgAAAAAA//tQxBqAAADSAAAAAAAAANIAAAAASMgxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYwxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjGMYxjAAAAAAAA==`;

/**
 * Custom Hook für Aufgaben-Benachrichtigungen
 * Prüft regelmäßig auf neue Aufgaben und zeigt Toast + Sound
 */
export const useTaskNotifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastCheckRef = useRef(new Date().toISOString());
  const audioRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Audio-Element initialisieren - mit try/catch für Mobile Kompatibilität
  useEffect(() => {
    try {
      if (!audioRef.current && typeof Audio !== 'undefined') {
        audioRef.current = new Audio(NOTIFICATION_SOUND_BASE64);
        audioRef.current.volume = 0.5;
      }
    } catch (e) {
      console.warn('Audio initialization failed (may be blocked on mobile):', e);
    }
  }, []);

  // Sound abspielen - mit defensivem Error-Handling
  const playNotificationSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(err => {
          // Browser blockiert möglicherweise Auto-Play - still ignorieren
        });
      }
    } catch (e) {
      // Audio nicht verfügbar - still ignorieren
    }
  }, []);

  // Auf neue Aufgaben prüfen
  const checkForNewTasks = useCallback(async () => {
    if (!user) return;

    try {
      const response = await axios.get(`${API}/tasks/new/since`, {
        params: { since: lastCheckRef.current }
      });

      const newTasks = response.data;

      if (newTasks && Array.isArray(newTasks) && newTasks.length > 0) {
        // Sound abspielen
        playNotificationSound();

        // Für jede neue Aufgabe eine Benachrichtigung anzeigen
        newTasks.forEach((task) => {
          if (!task || !task.id) return; // Defensive check
          
          const priorityText = task.priority === 'hoch' ? '🔴 DRINGEND: ' : '';
          const categoryLabels = {
            eigentuemer: 'Eigentümer',
            reinigung: 'Reinigung',
            reparatur: 'Reparatur'
          };

          toast.info(
            <div 
              className="cursor-pointer" 
              onClick={() => {
                navigate(`/tasks/${task.id}`);
                toast.dismiss();
              }}
            >
              <div className="font-semibold text-[#0E4C92]">
                {priorityText}Neue Aufgabe
              </div>
              <div className="text-sm font-medium mt-1">{task.title || 'Ohne Titel'}</div>
              <div className="text-xs text-stone-500 mt-1">
                {task.property_name || 'Unbekannt'} • {categoryLabels[task.category] || task.category || 'Sonstiges'}
              </div>
              <div className="text-xs text-stone-400 mt-2">
                Klicken zum Öffnen
              </div>
            </div>,
            {
              duration: Infinity,
              dismissible: true,
              id: `task-${task.id}`,
              className: 'task-notification',
            }
          );
        });
      }

      // Timestamp aktualisieren
      lastCheckRef.current = new Date().toISOString();
    } catch (error) {
      // Fehler still ignorieren (z.B. bei Netzwerkproblemen)
    }
  }, [user, navigate, playNotificationSound]);

  // Polling starten
  useEffect(() => {
    if (!user) return;

    // Beim ersten Start den Timestamp setzen (keine Benachrichtigung für bestehende Aufgaben)
    if (!isInitializedRef.current) {
      lastCheckRef.current = new Date().toISOString();
      isInitializedRef.current = true;
    }

    // Erste Prüfung nach kurzer Verzögerung (damit UI geladen ist)
    const initialTimeout = setTimeout(() => {
      checkForNewTasks();
    }, 5000);

    // Regelmäßiges Polling
    const interval = setInterval(checkForNewTasks, POLL_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [user, checkForNewTasks]);

  return { checkForNewTasks };
};

export default useTaskNotifications;
